/* Public Use of Funds share page — borrowers/brokers can fill out and
 * submit the Use of Funds Schedule via a dedicated share link.
 * Mirrors BalanceSheetPublicForm / PFSPublicForm UX. */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle } from 'lucide-react';
import UseOfFundsScheduleCard from './UseOfFundsScheduleCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UseOfFundsPublicForm = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const load = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/use-of-funds-link/${token}`);
      setInfo(resp.data);
      if (resp.data.used && (resp.data.items || []).length > 0) setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white border border-red-200 rounded-lg p-6 max-w-md text-center" data-testid="uof-public-error">
          <p className="text-red-700 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-[#081120] px-6 py-5">
          <h1 className="text-xl font-bold text-white" data-testid="uof-public-header">
            {info?.broker_company_name || 'Quidity Direct'}
          </h1>
          <p className="text-sm text-slate-300 mt-1">Use of Funds Schedule</p>
          {info?.business_name && (
            <p className="text-xs text-cyan-300 mt-2 font-semibold uppercase tracking-wide">{info.business_name}</p>
          )}
        </div>

        <div className="p-6 space-y-4">
          {submitted && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="uof-public-submitted">
              <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-900">Received</p>
                <p className="text-xs text-emerald-700 mt-1">
                  Your Use of Funds schedule has been submitted. You can still update it below if needed.
                </p>
              </div>
            </div>
          )}

          <p className="text-sm text-slate-600">
            Break down how the loan proceeds will be used. Add one line for each purpose (e.g., "Refinance OnDeck loan",
            "Purchase 2024 Freightliner", "Q4 marketing campaign"). Attach any supporting invoices or quotes to speed up
            underwriting.
          </p>

          <UseOfFundsScheduleCard
            token={token}
            linkKind="use_of_funds"
            items={info?.items || []}
            amountRequested={info?.amount_requested || 0}
            onSave={() => load()}
          />

          <p className="text-xs text-slate-500 text-center pt-4 border-t border-slate-100">
            Powered by Quidity Direct — Your data is transmitted over an encrypted connection.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UseOfFundsPublicForm;
